/**
 * /download/$slug/$ep — clean episode download page.
 *
 * Rules (v25-safe-link):
 *  - Every explicit download URL is accepted as-is.
 *  - The URL may be a protected /safelink generated link, a provider landing
 *    page, or a real direct file URL.
 *  - We do not scrape or force direct-download extraction; clicking download
 *    opens the stored link natively so the download/provider flow succeeds.
 */
import { createFileRoute, Link, useParams } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { Download, ArrowLeft, Loader2, AlertTriangle } from "lucide-react";
import { Header } from "@/components/rawan/Header";
import { Footer } from "@/components/rawan/Footer";
import { useI18n, t as tt } from "@/lib/i18n";
import { getEpisode, getSeriesBySlug } from "@/lib/content/loader";
import { applyOverrideToEpisode } from "@/lib/episode-overrides";
import { localizeEpisodeTitle } from "@/lib/episode-title";
import { startDirectDownload } from "@/lib/start-direct-download";

export const Route = createFileRoute("/download/$slug/$ep")({
  head: () => ({
    meta: [
      { title: "تحميل الحلقة · Download Episode · Télécharger — Rawan UI" },
      {
        name: "description",
        content:
          "صفحة تحميل الحلقة بجودات متعددة عبر بروكسي محمي — Multi-quality episode download page.",
      },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: DownloadEpisodePage,
});

type Row = {
  key: string;
  quality: string;
  server: string;
  shareUrl: string;
  filenameBase: string;
  size?: string;
};

function DownloadEpisodePage() {
  const { slug, ep } = useParams({ from: "/download/$slug/$ep" });
  const { language } = useI18n();
  const t = (ar: string, en: string, fr: string) => tt(language, ar, en, fr);

  const series = useMemo(() => getSeriesBySlug(slug), [slug]);
  const [season, episodeNumber] = parseEpisodeId(ep);
  const episode = useMemo(() => {
    const raw = episodeNumber ? getEpisode(slug, season, episodeNumber) : undefined;
    return raw ? applyOverrideToEpisode(slug, season, raw) : undefined;
  }, [slug, season, episodeNumber]);

  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setRows([]);

    async function run() {
      if (!episode) {
        setError(t("لم يتم العثور على الحلقة.", "Episode not found.", "Épisode introuvable."));
        setLoading(false);
        return;
      }

      const filenameBase = `${slug}-S${season}E${String(episodeNumber ?? 1).padStart(2, "0")}`;

      if (!episode.download_url) {
        setError(
          t(
            "لا يتوفر رابط تحميل لهذه الحلقة. أضف لكل جودة رابطها الخاص من لوحة التحكم، ويفضّل أن يكون رابطاً محمياً من مولّد الروابط.",
            "No download link for this episode. Add one URL per quality in the dashboard; protected Safelink URLs are recommended.",
            "Aucun lien de téléchargement. Ajoutez une URL par qualité depuis le tableau de bord; les liens protégés Safelink sont recommandés.",
          ),
        );
        setLoading(false);
        return;
      }

      try {
        const explicit = parseDownloadUrlField(episode.download_url);
        const resolved = explicit.map((entry, i): Row => ({
          key: `dl-${i}`,
          quality: entry.quality,
          server: entry.server || providerLabelFromUrl(entry.url),
          shareUrl: entry.url,
          filenameBase: `${filenameBase}-${entry.quality}`,
          size: entry.size,
        }));
        if (cancelled) return;
        if (!resolved.length) {
          setError(
            t(
              "روابط التحميل غير صالحة. تأكد أن كل رابط يبدأ بـ http أو https.",
              "Download links are invalid. Make sure every link starts with http or https.",
              "Les liens de téléchargement sont invalides. Vérifiez que chaque lien commence par http ou https.",
            ),
          );
        } else {
          setRows(resolved);
        }
      } catch (e) {
        if (!cancelled) setError(String((e as Error)?.message ?? e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void run();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, ep]);

  const epTitle = episode ? localizeEpisodeTitle(episode.title, language) : "";
  const seriesTitle =
    language === "en" && series?.title_en
      ? series.title_en
      : language === "fr" && series?.title_fr
        ? series.title_fr
        : (series?.title ?? slug);

  return (
    <div className="min-h-dvh flex flex-col bg-background text-foreground">
      <Header />
      <main className="flex-1 mx-auto w-full max-w-3xl px-4 py-6 space-y-5">
        <Link
          to="/series/$slug"
          params={{ slug }}
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          {t("العودة إلى المسلسل", "Back to series", "Retour à la série")}
        </Link>

        <header className="rounded-lg border border-border bg-card p-4">
          <div className="text-xs text-muted-foreground">{seriesTitle}</div>
          <h1 className="text-xl font-bold flex items-center gap-2">
            <Download className="h-5 w-5" />
            {epTitle || t("تحميل الحلقة", "Download episode", "Télécharger l'épisode")}
          </h1>
        </header>

        {error && (
          <div className="rounded-md border border-destructive/30 bg-destructive/10 text-destructive p-3 text-sm flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : rows.length ? (
          <section className="rounded-lg border border-border bg-card overflow-hidden">
            <table className="min-w-full text-sm">
              <thead className="bg-muted/50 text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="text-start p-3">{t("الجودة", "Quality", "Qualité")}</th>
                  <th className="text-start p-3">{t("الخادم", "Server", "Serveur")}</th>
                  <th className="text-start p-3">{t("الحجم", "Size", "Taille")}</th>
                  <th className="text-end p-3">{t("رابط", "Link", "Lien")}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.key} className="border-t border-border hover:bg-accent/30">
                    <td className="p-3 font-semibold">{r.quality}</td>
                    <td className="p-3">{r.server}</td>
                    <td className="p-3 text-xs text-muted-foreground">{r.size ?? "—"}</td>
                    <td className="p-3 text-end">
                      <DownloadButton
                        shareUrl={r.shareUrl}
                        filenameBase={r.filenameBase}
                        label={t("تحميل", "Download", "Télécharger")}
                        loadingLabel={t("جارٍ التجهيز…", "Preparing…", "Préparation…")}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        ) : null}

        <p className="text-[11px] text-muted-foreground">
          {t(
            "كل زر يفتح رابط الجودة المحفوظ كما هو. استخدم مولّد الرابط المحمي إذا أردت إخفاء الرابط الأصلي.",
            "Each button opens the saved quality URL as-is. Use the Safelink generator if you want to hide the original URL.",
            "Chaque bouton ouvre l'URL de qualité enregistrée telle quelle. Utilisez le générateur Safelink pour masquer l'URL originale.",
          )}
        </p>
      </main>
      <Footer />
    </div>
  );
}

function parseEpisodeId(raw: string): [number, number | null] {
  const m1 = raw.match(/[sS](\d+)[eE](\d+)/);
  if (m1) return [Number(m1[1]), Number(m1[2])];
  const m2 = raw.match(/^(\d+)x(\d+)$/);
  if (m2) return [Number(m2[1]), Number(m2[2])];
  const n = Number(raw);
  if (Number.isFinite(n) && n > 0) return [1, n];
  return [1, null];
}

type ParsedDownloadEntry = { quality: string; url: string; size?: string; server?: string };

function parseDownloadUrlField(raw: string): ParsedDownloadEntry[] {
  const trimmed = raw.trim();
  if (!trimmed) return [];

  try {
    const parsed = JSON.parse(trimmed) as unknown;
    if (Array.isArray(parsed)) {
      const entries = parsed
        .map((x, i): ParsedDownloadEntry | null => {
          if (typeof x === "string") return parseDownloadLine(x, i);
          if (!x || typeof x !== "object") return null;
          const o = x as Record<string, unknown>;
          const url = typeof o.url === "string" ? o.url : "";
          if (!/^https?:\/\//i.test(url)) return null;
          return {
            quality: typeof o.quality === "string" ? o.quality : `link-${i + 1}`,
            url,
            size: typeof o.size === "string" ? o.size : undefined,
            server: typeof o.server === "string" ? o.server : undefined,
          };
        })
        .filter((x): x is ParsedDownloadEntry => Boolean(x));
      if (entries.length) return entries;
    }
  } catch {
    /* plain text */
  }

  const lines = trimmed.split(/[\r\n]+/).map((s) => s.trim()).filter(Boolean);
  const entries = (lines.length ? lines : [trimmed])
    .map((line, i) => parseDownloadLine(line, i))
    .filter((x): x is ParsedDownloadEntry => Boolean(x));
  return entries.length ? entries : [{ quality: "auto", url: trimmed }];
}

function parseDownloadLine(line: string, index = 0): ParsedDownloadEntry | null {
  const url = line.match(/https?:\/\/\S+/i)?.[0]?.replace(/[),;]+$/, "");
  if (!url) return null;
  const before = line.slice(0, line.indexOf(url)).replace(/[|,؛:：-]+$/g, "").trim();
  const quality =
    before.match(/(?:4k|2160p|1440p|1080p|720p|480p|360p|auto)/i)?.[0] ??
    (index === 0 ? "auto" : `link-${index + 1}`);
  return { quality, url };
}

function providerLabelFromUrl(raw: string): string {
  try {
    const host = new URL(raw).hostname.toLowerCase();
    if (host.includes("mediafire")) return "MediaFire";
    if (host.includes("drive.google")) return "Google Drive";
    if (host.includes("dropbox")) return "Dropbox";
    if (host.includes("1drv") || host.includes("onedrive")) return "OneDrive";
    if (host.includes("mega")) return "MEGA";
    if (host.includes("terabox") || host.includes("1024tera")) return "TeraBox";
    if (host.includes("r2.dev") || host.includes("cloudflarestorage")) return "R2";
    if (typeof window !== "undefined" && host === window.location.hostname) return "Safelink";
    return host.replace(/^www\./, "");
  } catch {
    return "Server";
  }
}

function DownloadButton({
  shareUrl,
  filenameBase,
  label,
  loadingLabel,
}: {
  shareUrl: string;
  filenameBase: string;
  label: string;
  loadingLabel: string;
}) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  return (
    <div className="flex flex-col items-end gap-1">
      <button
        type="button"
        disabled={busy}
        onClick={async () => {
          setErr(null);
          setBusy(true);
          const res = await startDirectDownload(shareUrl, `${filenameBase}.mp4`);
          setBusy(false);
          if (!res.ok) setErr(res.error);
        }}
        className="inline-flex items-center gap-1 rounded-md bg-primary text-primary-foreground px-3 py-1.5 text-xs hover:bg-primary/90 disabled:opacity-60 disabled:cursor-wait"
      >
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
        {busy ? loadingLabel : label}
      </button>
      {err && <span className="text-[10px] text-destructive max-w-[220px] text-end">{err}</span>}
    </div>
  );
}
